import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Flame, HandHeart, BookOpen, Users, Sparkles, ArrowRight, Sunrise, Cross } from "lucide-react";
import TopWarriorsSection from "@/components/TopWarriorsSection";
import PrioritySection from "@/components/PrioritySection";

const HERO_IMG =
    "https://images.unsplash.com/photo-1699830506478-af7b3f5e6cc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxiZWF1dGlmdWwlMjBjaHVyY2glMjBpbnRlcmlvciUyMHdhcm0lMjBsaWdodHxlbnwwfHx8fDE3NzcyODU1Nzd8MA&ixlib=rb-4.1.0&q=85";
const ROSARY_IMG =
    "https://static.prod-images.emergentagent.com/jobs/ea5d9fcf-7240-4bd7-a6ee-19796942592e/images/453c3a96e598a08a0c170b0afaed6d07fdc36ea9cf8c3371be4ce7df3b3cb214.png";
const WARRIORS_IMG =
    "https://images.unsplash.com/photo-1641491549166-b5aaee13e432?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxjYXRob2xpYyUyMHlvdXRoJTIwcHJheWluZyUyMHJvc2FyeSUyMGNodXJjaHxlbnwwfHx8fDE3NzcyODU1Nzd8MA&ixlib=rb-4.1.0&q=85";

const intentions = [
    "Education & Exams",
    "To Build a House",
    "To Complete the Loan",
    "To Get a Job",
    "For Vocation",
    "For Marriage",
    "For Child Birth",
    "Business Development",
    "Peace in the Family",
    "Repose of the Soul",
];

const special = [
    { icon: <Cross size={18} />, title: "Pray the Rosary" },
    { icon: <BookOpen size={18} />, title: "Pray in the Mass" },
    { icon: <Sparkles size={18} />, title: "Offer Holy Mass" },
    { icon: <Flame size={18} />, title: "Pray in Adoration" },
    { icon: <HandHeart size={18} />, title: "Divine Mercy" },
    { icon: <Sunrise size={18} />, title: "Special Novena" },
];

export default function Landing() {
    const { t } = useTranslation();
    return (
        <div className="parchment">
            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="mx-auto max-w-7xl px-6 md:px-10 pt-16 pb-20 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7 fade-up">
                        <div className="text-xs tracking-[0.28em] uppercase mb-5" style={{ color: "#7F1D1D" }}>
                            {t("landing.kicker")}
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02]" style={{ color: "#1E1B4B" }}>
                            {t("landing.h1_a")}
                            <br />
                            {t("landing.h1_b")} <span style={{ color: "#7F1D1D" }}>{t("landing.h1_b_em")}</span> {t("landing.h1_b2")}
                            <br />
                            {t("landing.h1_c")}
                        </h1>
                        <p className="mt-6 max-w-xl text-base sm:text-lg" style={{ color: "#525252" }}>
                            {t("landing.subtitle")}
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="landing-cta-row">
                            <Link to="/register" className="btn-primary inline-flex items-center gap-2" data-testid="landing-join-warriors-btn">
                                {t("landing.cta_warriors")} <ArrowRight size={16} />
                            </Link>
                            <Link to="/intentions" className="btn-ghost inline-flex items-center gap-2" data-testid="landing-ask-prayer-btn">
                                {t("landing.cta_ask")}
                            </Link>
                        </div>
                        <div className="mt-10 flex items-center gap-6 text-sm" style={{ color: "#525252" }}>
                            <div>
                                <div className="font-display text-3xl" style={{ color: "#1E1B4B" }}>10+</div>
                                <div>{t("landing.stats_categories")}</div>
                            </div>
                            <div className="h-10 w-px" style={{ background: "#E5E0D8" }} />
                            <div>
                                <div className="font-display text-3xl" style={{ color: "#1E1B4B" }}>24/7</div>
                                <div>{t("landing.stats_praying")}</div>
                            </div>
                            <div className="h-10 w-px" style={{ background: "#E5E0D8" }} />
                            <div>
                                <div className="font-display text-3xl" style={{ color: "#1E1B4B" }}>∞</div>
                                <div>{t("landing.stats_goodness")}</div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-5 fade-up delay-2">
                        <div className="relative rounded-[2rem] overflow-hidden" style={{ border: "1px solid #E5E0D8" }}>
                            <img src={HERO_IMG} alt="Church interior with stained glass" className="w-full h-[460px] object-cover" />
                            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(30,27,75,0.10), rgba(30,27,75,0.45))" }} />
                            <div className="absolute bottom-6 left-6 right-6 p-4 rounded-2xl" style={{ background: "rgba(253,251,247,0.92)" }}>
                                <div className="text-xs tracking-[0.2em] uppercase" style={{ color: "#7F1D1D" }}>{t("landing.note_title")}</div>
                                <div className="font-display text-lg mt-1" style={{ color: "#1E1B4B" }}>
                                    {t("landing.note_body")}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="gold-divider mx-auto max-w-6xl" />
            </section>

            {/* Intentions chips */}
            <section className="mx-auto max-w-7xl px-6 md:px-10 py-16">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-xs tracking-[0.28em] uppercase" style={{ color: "#7F1D1D" }}>{t("landing.general_kicker")}</div>
                        <h2 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "#1E1B4B" }}>
                            {t("landing.general_h")}
                        </h2>
                        <p className="mt-3 max-w-2xl" style={{ color: "#525252" }}>
                            {t("landing.general_sub")}
                        </p>
                    </div>
                    <Link to="/intentions" className="btn-gold inline-flex items-center gap-2" data-testid="landing-intentions-link">
                        {t("landing.open_board")} <ArrowRight size={16} />
                    </Link>
                </div>

                <div className="mt-8 flex flex-wrap gap-2.5" data-testid="landing-intention-chips">
                    {intentions.map((label) => (
                        <span key={label} className="chip" data-testid={`landing-chip-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            {label}
                        </span>
                    ))}
                    <span className="chip active">{t("landing.write_own")}</span>
                </div>
            </section>

            {/* Priority / Top demanding prayers */}
            <PrioritySection />

            {/* Top Warriors */}
            <TopWarriorsSection />

            {/* Special Request Model */}
            <section className="mx-auto max-w-7xl px-6 md:px-10 py-16">
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-5">
                        <div className="relative rounded-[2rem] overflow-hidden" style={{ border: "1px solid #E5E0D8" }}>
                            <img src={ROSARY_IMG} alt="Rosary and bible" className="w-full h-[440px] object-cover" />
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="text-xs tracking-[0.28em] uppercase" style={{ color: "#7F1D1D" }}>{t("landing.special_kicker")}</div>
                        <h2 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "#1E1B4B" }}>
                            {t("landing.special_h")}
                        </h2>
                        <p className="mt-3" style={{ color: "#525252" }}>
                            {t("landing.special_sub")}
                        </p>
                        <div className="mt-8 grid sm:grid-cols-2 gap-3">
                            {special.map((s) => (
                                <div key={s.title} className="p-card px-4 py-3 flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ background: "#F5F0E6", color: "#7F1D1D" }}>
                                        {s.icon}
                                    </div>
                                    <div className="font-display text-lg" style={{ color: "#1E1B4B" }}>{s.title}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-6 text-sm" style={{ color: "#525252" }}>
                            {t("landing.special_novenas_note")}
                        </div>
                        <Link to="/special" className="mt-6 inline-flex btn-primary items-center gap-2" data-testid="landing-special-link">
                            {t("landing.special_cta")} <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Warriors */}
            <section className="mx-auto max-w-7xl px-6 md:px-10 py-16">
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        <div className="text-xs tracking-[0.28em] uppercase" style={{ color: "#7F1D1D" }}>{t("landing.warriors_kicker")}</div>
                        <h2 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "#1E1B4B" }}>
                            {t("landing.warriors_h")}
                        </h2>
                        <p className="mt-4 max-w-2xl" style={{ color: "#525252" }}>
                            {t("landing.warriors_sub")}
                        </p>
                        <div className="mt-8 grid sm:grid-cols-3 gap-3">
                            {[
                                { icon: <Users size={18} />, t: t("landing.warriors_a"), d: t("landing.warriors_a_d") },
                                { icon: <Sunrise size={18} />, t: t("landing.warriors_b"), d: t("landing.warriors_b_d") },
                                { icon: <HandHeart size={18} />, t: t("landing.warriors_c"), d: t("landing.warriors_c_d") },
                            ].map((x, i) => (
                                <div key={i} className="p-card p-5">
                                    <div className="h-10 w-10 rounded-full flex items-center justify-center mb-3" style={{ background: "#1E1B4B", color: "#D4AF37" }}>{x.icon}</div>
                                    <div className="font-display text-xl" style={{ color: "#1E1B4B" }}>{x.t}</div>
                                    <div className="text-sm mt-1" style={{ color: "#525252" }}>{x.d}</div>
                                </div>
                            ))}
                        </div>
                        <Link to="/join-warriors" className="mt-8 inline-flex btn-primary items-center gap-2" data-testid="landing-warriors-link">
                            {t("landing.warriors_cta")} <ArrowRight size={16} />
                        </Link>
                    </div>
                    <div className="lg:col-span-5 order-1 lg:order-2">
                        <div className="relative rounded-[2rem] overflow-hidden" style={{ border: "1px solid #E5E0D8" }}>
                            <img src={WARRIORS_IMG} alt="Youth praying rosary" className="w-full h-[440px] object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Angels on Earth */}
            <section className="mx-auto max-w-7xl px-6 md:px-10 py-16">
                <div className="p-card p-8 md:p-12 text-center relative overflow-hidden">
                    <div className="text-xs tracking-[0.28em] uppercase" style={{ color: "#7F1D1D" }}>{t("landing.angels_kicker")}</div>
                    <h2 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "#1E1B4B" }}>
                        {t("landing.angels_h")}
                    </h2>
                    <p className="mt-3 max-w-2xl mx-auto" style={{ color: "#525252" }}>
                        {t("landing.angels_sub")}
                    </p>
                    <Link to="/testimonies" className="mt-8 inline-flex btn-gold items-center gap-2" data-testid="landing-testimonies-link">
                        {t("landing.angels_cta")} <ArrowRight size={16} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
